import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  InputAdornment,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../utils/axios';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Events & settings for Campus Ministry. Each event is one yearly occurrence
// of a form (e.g. Fall Retreat 2026) with its own prices, dates, consent text
// and notification recipients.
const CampusMinistrySettings = () => {
  const [events, setEvents] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [original, setOriginal] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ formType: 'fall-retreat', year: new Date().getFullYear() });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const hasChanges = editing && JSON.stringify(editing) !== JSON.stringify(original);

  const fetchEvents = useCallback(async () => {
    try {
      const [eventsRes, typesRes] = await Promise.all([
        axios.get('/campus-ministry/events'),
        axios.get('/campus-ministry/events/form-types')
      ]);
      setEvents(eventsRes.data || []);
      setFormTypes(typesRes.data || []);
      return eventsRes.data || [];
    } catch (err) {
      console.error('Error fetching Campus Ministry events:', err);
      setError('Error loading events');
      return [];
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const startEditing = (event) => {
    const copy = JSON.parse(JSON.stringify(event));
    setEditing(copy);
    setOriginal(JSON.parse(JSON.stringify(event)));
    setNewRecipient('');
  };

  const setField = (path, value) => {
    setEditing(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else { if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; }
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: editing.name,
        registrationEnabled: editing.registrationEnabled,
        isCurrent: editing.isCurrent,
        pricing: {
          rowanUndergrad: Number(editing.pricing?.rowanUndergrad) || 0,
          otherStudents: Number(editing.pricing?.otherStudents) || 0
        },
        minimumOnlinePayment: Math.max(1, Number(editing.minimumOnlinePayment) || 1),
        schedule: editing.schedule,
        parishName: editing.parishName,
        organizationName: editing.organizationName,
        hardshipMessage: editing.hardshipMessage,
        consentText: editing.consentText,
        notificationRecipients: editing.notificationRecipients || []
      };
      const res = await axios.put(`/campus-ministry/events/${editing._id}`, payload);
      const list = await fetchEvents();
      const fresh = list.find(e => e._id === res.data._id) || res.data;
      startEditing(fresh);
      setSuccess('Event saved');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await axios.post('/campus-ministry/events', createForm);
      setCreateOpen(false);
      const list = await fetchEvents();
      startEditing(list.find(e => e._id === res.data._id) || res.data);
      setSuccess(`${res.data.name} created. Set its dates, prices and recipients, then open registration.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create event');
    }
  };

  const handleNextYear = async (event) => {
    setError(null);
    try {
      const res = await axios.post(`/campus-ministry/events/${event._id}/next-year`);
      const list = await fetchEvents();
      startEditing(list.find(e => e._id === res.data._id) || res.data);
      setSuccess(`${res.data.name} created from ${event.name}. It is closed until you open it.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create next year\'s event');
    }
  };

  const handleDelete = async () => {
    const event = confirmDelete;
    setConfirmDelete(null);
    try {
      await axios.delete(`/campus-ministry/events/${event._id}`);
      if (editing?._id === event._id) {
        setEditing(null);
        setOriginal(null);
      }
      await fetchEvents();
      setSuccess('Event deleted');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete event');
    }
  };

  const addRecipient = () => {
    const email = newRecipient.trim();
    if (!EMAIL_RE.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    const list = editing.notificationRecipients || [];
    if (list.includes(email)) {
      setError('That email is already on the list');
      return;
    }
    setField('notificationRecipients', [...list, email]);
    setNewRecipient('');
  };

  const publicLink = (formType) => `${window.location.origin}/campus-ministry/${formType}/register`;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h5">Campus Ministry Events &amp; Settings</Typography>
          <Button variant="outlined" component={RouterLink} to="/campus-ministry" startIcon={<ArrowBackIcon />}>
            Back to Registrations
          </Button>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Each event is one year of a signup form. Use &ldquo;Next year&rdquo; to copy an event (prices, consent text, recipients) to the following year.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New Event
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Prices</TableCell>
                <TableCell>Registrations</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No events yet. Click &ldquo;New Event&rdquo; to create Fall Retreat {new Date().getFullYear()}.
                  </TableCell>
                </TableRow>
              )}
              {events.map(ev => (
                <TableRow key={ev._id} selected={editing?._id === ev._id} hover>
                  <TableCell>{ev.name}</TableCell>
                  <TableCell>{ev.year}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {ev.isCurrent && <Chip size="small" color="primary" label="Current" />}
                      <Chip size="small" color={ev.registrationEnabled ? 'success' : 'default'} label={ev.registrationEnabled ? 'Open' : 'Closed'} />
                    </Stack>
                  </TableCell>
                  <TableCell>${Number(ev.pricing?.rowanUndergrad || 0).toFixed(2)} / ${Number(ev.pricing?.otherStudents || 0).toFixed(2)}</TableCell>
                  <TableCell>{ev.registrationCount || 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => startEditing(ev)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Button size="small" onClick={() => handleNextYear(ev)}>Next year</Button>
                    {(ev.registrationCount || 0) === 0 && (
                      <Tooltip title="Delete (only events with no registrations)">
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(ev)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {editing && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Edit: {original?.name}</Typography>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!hasChanges || saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <FormControlLabel
                control={<Switch checked={!!editing.registrationEnabled} onChange={(e) => setField('registrationEnabled', e.target.checked)} />}
                label="Registration open"
              />
              <FormControlLabel
                control={<Switch checked={!!editing.isCurrent} onChange={(e) => setField('isCurrent', e.target.checked)} />}
                label="Current event (shown at the public link)"
              />
            </Stack>
            {editing.isCurrent && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Public link: {publicLink(editing.formType)}</Typography>
                <IconButton size="small" onClick={() => navigator.clipboard?.writeText(publicLink(editing.formType))}><CopyIcon fontSize="small" /></IconButton>
              </Stack>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Event Name" value={editing.name || ''} onChange={(e) => setField('name', e.target.value)} helperText="Shown as the form title and in emails" />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Prices</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Rowan University undergraduate" value={editing.pricing?.rowanUndergrad ?? 0}
                onChange={(e) => setField('pricing.rowanUndergrad', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="All other students" value={editing.pricing?.otherStudents ?? 0}
                onChange={(e) => setField('pricing.otherStudents', e.target.value)}
                helperText="Rowan graduate students, RCSJ, RCBC, etc."
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Minimum online payment" value={editing.minimumOnlinePayment ?? 1}
                onChange={(e) => setField('minimumOnlinePayment', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { min: 1, step: '1' } }} />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Dates (shown on the form)</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Dates of the event (departure and return)" placeholder="10/3/26 at 12pm – 10/4/26 at 9pm"
                value={editing.schedule?.dates || ''} onChange={(e) => setField('schedule.dates', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Optional portion (if any)" placeholder="10/4/26 at 9pm – 10/5/26 at 7am"
                value={editing.schedule?.optionalDates || ''} onChange={(e) => setField('schedule.optionalDates', e.target.value)} />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Consent Form</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Parish" value={editing.parishName || ''} onChange={(e) => setField('parishName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Youth/Young Adult Organization" value={editing.organizationName || ''} onChange={(e) => setField('organizationName', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={6} label="Consent to Medical Treatment, Release and Indemnity (text)"
                value={editing.consentText || ''} onChange={(e) => setField('consentText', e.target.value)}
                helperText="Separate paragraphs with a blank line. Each registration keeps a copy of the text the participant agreed to." />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Payment Step Message</Typography>
          <TextField fullWidth multiline minRows={2} label="Message shown next to the hardship option" sx={{ mb: 3 }}
            value={editing.hardshipMessage || ''} onChange={(e) => setField('hardshipMessage', e.target.value)} />

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>New Registration Notifications</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These staff members get an email for each new signup (no medical details — just a link to the portal). They also receive alerts if an online payment needs manual follow-up.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {(editing.notificationRecipients || []).map(email => (
              <Chip key={email} label={email} onDelete={() => setField('notificationRecipients', editing.notificationRecipients.filter(r => r !== email))} />
            ))}
            {(editing.notificationRecipients || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">No recipients yet.</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ maxWidth: 480 }}>
            <TextField size="small" fullWidth label="Add email" value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addRecipient(); }} />
            <Button variant="outlined" onClick={addRecipient}>Add</Button>
          </Stack>

          {editing.auditLog && editing.auditLog.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">Last change: {editing.auditLog[editing.auditLog.length - 1].user?.name} · {new Date(editing.auditLog[editing.auditLog.length - 1].timestamp).toLocaleString()}</Typography>
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Event</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Form" value={createForm.formType} sx={{ mt: 1, mb: 2 }}
            onChange={(e) => setCreateForm({ ...createForm, formType: e.target.value })}>
            {formTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField fullWidth type="number" label="Year" value={createForm.year}
            onChange={(e) => setCreateForm({ ...createForm, year: e.target.value })} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The new event starts closed. The consent text and hardship message are pre-filled from the paper form.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>
          <Typography>Delete {confirmDelete?.name}? It has no registrations. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={5000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default CampusMinistrySettings;
